import React, { useEffect, useRef, useState } from 'react'
import {
  Card,
  Row,
  Col,
  Statistic,
  Button,
  Tabs,
  List,
  Avatar,
  Tag,
  Space,
  Modal,
  message,
  Badge,
  Typography,
  Spin,
  Upload,
  Input
} from 'antd'
import {
  LogoutOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  AuditOutlined,
  ClockCircleOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { useVehicleStore } from '../stores/vehicleStore'
import { simpleDirectCloudApi } from '../services/directCloudApi.simple'
import { app, ensureLogin } from '../services/tcbClient'
import { formatPeriodLabel, getVehicleDueDate, isLeaseStarted, getAdjustmentRevenueDelta } from './vehicleManagerUtils'

const { Text, Title } = Typography

const CANVAS_HEIGHT = 180
type DrawPoint = { x: number; y: number }

const paidStatuses = ['cashier_done', 'accounted', 'completed']

const CashierPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('pending')
  const [loading, setLoading] = useState(true)
  const [pendingReceipts, setPendingReceipts] = useState<any[]>([])
  const [verifiedReceipts, setVerifiedReceipts] = useState<any[]>([])
  const [statistics, setStatistics] = useState({
    pendingVerification: 0,
    verifiedToday: 0,
    pendingAmount: 0,
    totalProcessed: 0
  })
  const [confirmVisible, setConfirmVisible] = useState(false)
  const [currentReceipt, setCurrentReceipt] = useState<any>(null)
  const [receiptPreviewVisible, setReceiptPreviewVisible] = useState(false)
  const [receiptPreview, setReceiptPreview] = useState<any>(null)
  const [receiptPreviewMode, setReceiptPreviewMode] = useState<'pending' | 'verified'>('pending')
  const [receiptSignatureUrl, setReceiptSignatureUrl] = useState('')
  const [signatureList, setSignatureList] = useState<any[]>([])
  const [cashierNotes, setCashierNotes] = useState('')
  const [signatureDataUrl, setSignatureDataUrl] = useState<string>('')
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const receiptDrawingRef = useRef(false)
  const receiptLastPointRef = useRef<DrawPoint | null>(null)
  const [leaseForms, setLeaseForms] = useState<any[]>([])
  const [signedLeaseForms, setSignedLeaseForms] = useState<any[]>([])
  const [leasePreviewVisible, setLeasePreviewVisible] = useState(false)
  const [leasePreview, setLeasePreview] = useState<any>(null)
  const [leaseConfirmVisible, setLeaseConfirmVisible] = useState(false)
  const [currentLeaseForm, setCurrentLeaseForm] = useState<any>(null)
  const [leaseSignatureUrl, setLeaseSignatureUrl] = useState('')
  const [leaseSignatureList, setLeaseSignatureList] = useState<any[]>([])
  const [leaseSignatureDataUrl, setLeaseSignatureDataUrl] = useState<string>('')
  const [leaseNotes, setLeaseNotes] = useState('')
  const leaseCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const leaseDrawingRef = useRef(false)
  const leaseLastPointRef = useRef<DrawPoint | null>(null)
  const receiptPrintRef = useRef<HTMLDivElement | null>(null)
  const [expiringModalVisible, setExpiringModalVisible] = useState(false)

  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const { vehicles, fetchVehicles } = useVehicleStore()

  useEffect(() => {
    loadData()
  }, [])

  const normalizeReceipts = (list: any[]) =>
    list.map(r => {
      const items = getReceiptItems(r)
      const total = items.reduce((s: number, it: any) => s + (Number(it.expectedAmount ?? it.amount ?? 0) || 0), 0)
      const received = items.reduce((s: number, it: any) => s + (Number(it.receivedAmount ?? it.verifiedAmount ?? it.amount ?? 0) || 0), 0)
      const first = items[0] || {}
      return {
        ...r,
        receiptId: r._id || r.id,
        receiptStatus: r.status,
        amount: total || Number(r.amount || 0) || 0,
        verifiedAmount: received || Number(r.verifiedAmount || 0) || 0,
        vehicleId: r.vehicleId || first.vehicleId,
        plate: r.plate || first.plate || r.company || '合并收款',
        model: r.model || first.model,
        company: r.company || first.company,
        contactPerson: r.contactPerson || first.contactPerson,
        dueDate: r.dueDate || first.dueDate,
        chargeRangeStart: r.chargeRangeStart || first.chargeRangeStart,
        chargeRangeEnd: r.chargeRangeEnd || first.chargeRangeEnd,
        periodStart: r.periodStart || first.periodStart,
        periodEnd: r.periodEnd || first.periodEnd,
        rentPeriod: r.rentPeriod || first.rentPeriod,
        rawReceipt: r
      }
    })

  const getRangeLabel = (item: any) => {
    const start = item.chargeRangeStart || item.periodStart || item.startDate
    const end = item.chargeRangeEnd || item.periodEnd || item.dueDate
    if (!start || !end) return ''
    const startDay = dayjs(start)
    const endDay = dayjs(end)
    if (!startDay.isValid() || !endDay.isValid()) return ''
    return `${startDay.format('YYYY-MM-DD')} ~ ${endDay.format('YYYY-MM-DD')}`
  }

  const getPeriodLabel = (value?: string, months?: number) => formatPeriodLabel(value, months)

  const getReceiptItems = (receipt: any) => {
    const raw = receipt?.items
    if (Array.isArray(raw)) return raw
    if (raw && typeof raw === 'object') return Object.values(raw)
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw)
        if (Array.isArray(parsed)) return parsed
        if (parsed && typeof parsed === 'object') return Object.values(parsed)
      } catch {
        return []
      }
    }
    return []
  }

  const getReceiptSignatureUrl = (receipt: any) => {
    const raw = receipt?.rawReceipt || receipt
    if (raw?.cashierSignatureUrl) return raw.cashierSignatureUrl
    const items = getReceiptItems(raw)
    const firstSigned = items.find((it: any) => it.cashierSignatureUrl)
    return firstSigned?.cashierSignatureUrl || ''
  }

  const resolveReceiptSignatureUrl = async (receipt: any) => {
    const rawUrl = getReceiptSignatureUrl(receipt)
    if (!rawUrl) {
      setReceiptSignatureUrl('')
      return
    }
    if (!String(rawUrl).startsWith('cloud://')) {
      setReceiptSignatureUrl(rawUrl)
      return
    }
    try {
      await ensureLogin()
      const storage = (app as any).storage?.()
      const useStorage = Boolean(storage?.getTempFileURL)
      const fileList = [{ fileID: rawUrl, maxAge: 24 * 60 * 60 }]
      const res: any = useStorage
        ? await storage.getTempFileURL({ fileList })
        : await (app as any).getTempFileURL({ fileList })
      const tempUrl =
        res?.fileList?.[0]?.tempFileURL ||
        res?.fileList?.[0]?.tempFileUrl ||
        res?.fileList?.[0]?.download_url
      if (tempUrl) {
        setReceiptSignatureUrl(tempUrl)
      } else {
        setReceiptSignatureUrl('')
      }
    } catch (err) {
      console.error(err)
      setReceiptSignatureUrl('')
    }
  }

  const openReceiptPreview = (item: any, mode: 'pending' | 'verified') => {
    setReceiptPreview(item)
    setReceiptPreviewVisible(true)
    setReceiptPreviewMode(mode)
  }

  const resolveLeaseSignatureUrl = async (lease: any) => {
    const rawUrl = lease?.cashierSignatureUrl || ''
    if (!rawUrl) {
      setLeaseSignatureUrl('')
      return
    }
    if (!String(rawUrl).startsWith('cloud://')) {
      setLeaseSignatureUrl(rawUrl)
      return
    }
    try {
      await ensureLogin()
      const storage = (app as any).storage?.()
      const useStorage = Boolean(storage?.getTempFileURL)
      const fileList = [{ fileID: rawUrl, maxAge: 24 * 60 * 60 }]
      const res: any = useStorage
        ? await storage.getTempFileURL({ fileList })
        : await (app as any).getTempFileURL({ fileList })
      const tempUrl =
        res?.fileList?.[0]?.tempFileURL ||
        res?.fileList?.[0]?.tempFileUrl ||
        res?.fileList?.[0]?.download_url
      if (tempUrl) {
        setLeaseSignatureUrl(tempUrl)
      } else {
        setLeaseSignatureUrl('')
      }
    } catch (err) {
      console.error(err)
      setLeaseSignatureUrl('')
    }
  }

  useEffect(() => {
    if (!receiptPreviewVisible || !receiptPreview) return
    resolveReceiptSignatureUrl(receiptPreview)
  }, [receiptPreviewVisible, receiptPreview])

  useEffect(() => {
    if (!leasePreviewVisible || !leasePreview) return
    resolveLeaseSignatureUrl(leasePreview)
  }, [leasePreviewVisible, leasePreview])

  const syncCanvasSize = (ref: React.RefObject<HTMLCanvasElement>) => {
    const canvas = ref.current
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    const ratio = window.devicePixelRatio || 1
    canvas.width = Math.max(1, Math.round(rect.width * ratio))
    canvas.height = Math.max(1, Math.round(rect.height * ratio))
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0)
    ctx.lineWidth = 3.2
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.imageSmoothingEnabled = true
    ctx.globalCompositeOperation = 'source-over'
  }

  useEffect(() => {
    if (!confirmVisible) return
    syncCanvasSize(canvasRef)
    const onResize = () => syncCanvasSize(canvasRef)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [confirmVisible])

  useEffect(() => {
    if (!leaseConfirmVisible) return
    syncCanvasSize(leaseCanvasRef)
    const onResize = () => syncCanvasSize(leaseCanvasRef)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [leaseConfirmVisible])

  const formatDate = (value?: string) => {
    const d = value ? dayjs(value) : dayjs()
    return d.isValid() ? d.format('YYYY-MM-DD') : ''
  }

  const formatMoney = (value: any) => Number(value || 0).toFixed(2)

  const getExpiringVehicles = (limitDays?: number) => {
    const now = dayjs()
    const pendingIds = pendingReceipts.map(p => p.vehicleId || p.plate)
    return vehicles
      .filter(v => v.status === 'rented')
      .filter(v => getVehicleDueDate(v))
      .filter(v => isLeaseStarted(v, now))
      .filter(v => !pendingIds.includes(v.id) && !pendingIds.includes(v._id))
      .filter(v => {
        if (limitDays === undefined) return true
        const diff = dayjs(getVehicleDueDate(v)).diff(now, 'day')
        return diff <= limitDays && diff >= 0
      })
      .sort((a, b) => dayjs(getVehicleDueDate(a)).diff(dayjs(getVehicleDueDate(b))))
  }

  const expiringVehiclesAll = getExpiringVehicles()
  const expiringTop = expiringVehiclesAll.slice(0, 3)
  const pendingReturnForms = leaseForms.filter((item: any) => item.type === 'return')
  const signedReturnVehicleIds = new Set(
    signedLeaseForms
      .filter((item: any) => item.type === 'return')
      .map((item: any) => String(item.vehicleId || item.plate || ''))
      .filter(Boolean)
  )
  const maintenanceWithoutSignedReturnDoc = vehicles.filter((v: any) => {
    if (v.status !== 'maintenance') return false
    const key = String(v._id || v.id || v.plate || '')
    const plate = String(v.plate || '')
    return !signedReturnVehicleIds.has(key) && !signedReturnVehicleIds.has(plate)
  })

  const handleExportReceipt = () => {
    if (!receiptPrintRef.current) return
    const content = receiptPrintRef.current.innerHTML
    const win = window.open('', '_blank')
    if (!win) return
    win.document.write(`
      <html>
        <head>
          <title>receipt</title>
          <style>
            body { margin: 24px; font-family: "SimSun","STSong","Noto Serif SC",serif; color: #222; }
            img { max-width: 100%; }
            @media print {
              body { margin: 0; }
            }
          </style>
        </head>
        <body>${content}</body>
      </html>
    `)
    win.document.close()
    win.focus()
    win.print()
  }

  const mapWithVehicle = (list: any[]) =>
    list.map(r => {
      const v = vehicles.find(vh => vh.id === r.vehicleId || vh._id === r.vehicleId || vh.plate === r.plate)
      const reminderDue = v ? getVehicleDueDate(v) : r.dueDate
      return {
        ...r,
        plate: r.plate || v?.plate || '未知车牌',
        model: r.model || v?.type || '',
        company: r.company || v?.company || '',
        contactPerson: r.contactPerson || v?.contactPerson || '',
        amount: r.amount ?? v?.rentAmount ?? 0,
        dueDate: r.dueDate || v?.dueDate,
        reminderDue,
        reminderAmount: v?.reminderAmount ?? v?.rentAmount,
        reminderPeriod: v?.reminderPeriod || v?.rentPeriod,
        reminderPeriodMonths: v?.reminderPeriodMonths
      }
    })

  const loadData = async () => {
    try {
      setLoading(true)
      await fetchVehicles().catch(() => {})
      const [pendingRes, allRes, adjustmentsRes, leaseRes] = await Promise.all([
        simpleDirectCloudApi.getReceipts('submitted'),
        simpleDirectCloudApi.getReceipts(),
        simpleDirectCloudApi.getVehicleAdjustments(),
        simpleDirectCloudApi.getLeaseForms()
      ])

      const pendingRaw = pendingRes.success && pendingRes.data ? pendingRes.data : []
      const allRaw = allRes.success && allRes.data ? allRes.data : []
      const verifiedRaw = allRaw.filter((r: any) => paidStatuses.includes(r.status))
      const adjustmentsRaw = adjustmentsRes.success && adjustmentsRes.data ? adjustmentsRes.data : []
      const adjustmentsClean = adjustmentsRaw.filter((adj: any) => adj.type !== 'rent_addition')
      const leaseRaw = leaseRes.success && leaseRes.data ? leaseRes.data : []
      const pendingLease = leaseRaw.filter((l: any) => l.status !== 'signed')
      const signedLease = leaseRaw.filter((l: any) => l.status === 'signed')

      const pending = mapWithVehicle(normalizeReceipts(pendingRaw))
      const verified = mapWithVehicle(normalizeReceipts(verifiedRaw))

      const today = new Date().toDateString()
      const verifiedToday = verified.filter(r => {
        const t = r.verifiedAt || r.accountedAt || r.updatedAt || r.createdAt
        if (!t) return false
        return new Date(t).toDateString() === today
      }).length
      const pendingAmount = pending.reduce((s: number, r: any) => s + (Number(r.amount) || 0), 0)
      const adjustmentsDelta = getAdjustmentRevenueDelta(adjustmentsClean)
      const totalProcessed = verified.reduce((s: number, r: any) => s + (Number(r.amount) || 0), 0) + adjustmentsDelta

      setPendingReceipts(pending)
      setVerifiedReceipts(verified)
      setLeaseForms(pendingLease)
      setSignedLeaseForms(signedLease)
      setStatistics({
        pendingVerification: pending.length,
        verifiedToday,
        pendingAmount,
        totalProcessed
      })
    } catch (error) {
      console.error(error)
      message.error('加载数据失败')
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = () => {
    Modal.confirm({
      title: '确认退出？',
      onOk: () => {
        logout()
        navigate('/login')
      }
    })
  }

  const handleConfirmReceipt = (receipt: any) => {
    setCurrentReceipt(receipt)
    setSignatureList([])
    setSignatureDataUrl('')
    setCashierNotes('')
    clearPad()
    setConfirmVisible(true)
  }

  const openLeasePreview = (item: any) => {
    setLeasePreview(item)
    setLeasePreviewVisible(true)
  }

  const handleConfirmLease = (item: any) => {
    setCurrentLeaseForm(item)
    setLeaseSignatureList([])
    setLeaseSignatureDataUrl('')
    setLeaseNotes('')
    clearLeasePad()
    setLeaseConfirmVisible(true)
  }

  const getPos = (
    e: React.MouseEvent<HTMLCanvasElement> |
      React.TouchEvent<HTMLCanvasElement> |
      React.PointerEvent<HTMLCanvasElement>,
    ref: React.RefObject<HTMLCanvasElement> = canvasRef
  ) => {
    const canvas = ref.current
    if (!canvas) return { x: 0, y: 0 }
    const rect = canvas.getBoundingClientRect()
    if ('touches' in e) {
      const t = e.touches[0]
      return { x: t.clientX - rect.left, y: t.clientY - rect.top }
    }
    if ('clientX' in e && 'clientY' in e) {
      return { x: e.clientX - rect.left, y: e.clientY - rect.top }
    }
    return { x: (e as React.MouseEvent).clientX - rect.left, y: (e as React.MouseEvent).clientY - rect.top }
  }

  const startStroke = (
    e: React.PointerEvent<HTMLCanvasElement>,
    ref: React.RefObject<HTMLCanvasElement>,
    drawingRef: React.MutableRefObject<boolean>,
    lastPointRef: React.MutableRefObject<DrawPoint | null>
  ) => {
    e.preventDefault()
    const canvas = ref.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    canvas.setPointerCapture?.(e.pointerId)
    const { x, y } = getPos(e, ref)
    ctx.strokeStyle = '#000'
    ctx.fillStyle = '#000'
    drawingRef.current = true
    lastPointRef.current = { x, y }
    // Draw a solid starting point so quick taps are not lost.
    ctx.beginPath()
    ctx.arc(x, y, Math.max(1.2, ctx.lineWidth / 2), 0, Math.PI * 2)
    ctx.fill()
  }

  const moveStroke = (
    e: React.PointerEvent<HTMLCanvasElement>,
    ref: React.RefObject<HTMLCanvasElement>,
    drawingRef: React.MutableRefObject<boolean>,
    lastPointRef: React.MutableRefObject<DrawPoint | null>
  ) => {
    if (!drawingRef.current) return
    e.preventDefault()
    const canvas = ref.current
    const ctx = canvas?.getContext('2d')
    if (!ctx) return
    const { x, y } = getPos(e, ref)
    const prev = lastPointRef.current
    if (!prev) {
      lastPointRef.current = { x, y }
      return
    }
    const dx = x - prev.x
    const dy = y - prev.y
    const distance = Math.hypot(dx, dy)
    if (distance < 0.001) return
    const steps = Math.max(1, Math.ceil(distance / 1.6))
    ctx.beginPath()
    ctx.moveTo(prev.x, prev.y)
    for (let i = 1; i <= steps; i += 1) {
      const t = i / steps
      const nx = prev.x + dx * t
      const ny = prev.y + dy * t
      ctx.lineTo(nx, ny)
    }
    ctx.stroke()
    lastPointRef.current = { x, y }
  }

  const endStroke = (
    ref: React.RefObject<HTMLCanvasElement>,
    drawingRef: React.MutableRefObject<boolean>,
    lastPointRef: React.MutableRefObject<DrawPoint | null>,
    setDataUrl: (value: string) => void
  ) => {
    if (!drawingRef.current) return
    drawingRef.current = false
    lastPointRef.current = null
    const canvas = ref.current
    if (canvas) {
      setDataUrl(canvas.toDataURL('image/png'))
    }
  }

  const startDraw = (e: React.PointerEvent<HTMLCanvasElement>) => {
    startStroke(e, canvasRef, receiptDrawingRef, receiptLastPointRef)
  }

  const draw = (e: React.PointerEvent<HTMLCanvasElement>) => {
    moveStroke(e, canvasRef, receiptDrawingRef, receiptLastPointRef)
  }

  const endDraw = () => {
    endStroke(canvasRef, receiptDrawingRef, receiptLastPointRef, setSignatureDataUrl)
  }

  const clearPad = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    setSignatureDataUrl('')
    receiptDrawingRef.current = false
    receiptLastPointRef.current = null
  }

  const startLeaseDraw = (e: React.PointerEvent<HTMLCanvasElement>) => {
    startStroke(e, leaseCanvasRef, leaseDrawingRef, leaseLastPointRef)
  }

  const drawLease = (e: React.PointerEvent<HTMLCanvasElement>) => {
    moveStroke(e, leaseCanvasRef, leaseDrawingRef, leaseLastPointRef)
  }

  const endLeaseDraw = () => {
    endStroke(leaseCanvasRef, leaseDrawingRef, leaseLastPointRef, setLeaseSignatureDataUrl)
  }

  const clearLeasePad = () => {
    const canvas = leaseCanvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    setLeaseSignatureDataUrl('')
    leaseDrawingRef.current = false
    leaseLastPointRef.current = null
  }

  const handleSubmitConfirm = async () => {
    if (!currentReceipt) return
    if ((!signatureList.length || !signatureList[0].originFileObj) && !signatureDataUrl) {
      message.error('请上传签名')
      return
    }
    try {
      const receiptKey = currentReceipt.receiptId || currentReceipt._id || currentReceipt.id || currentReceipt.plate
      let signatureUrl = ''
      if (signatureDataUrl) {
        const blob = await (await fetch(signatureDataUrl)).blob()
        const file = new File([blob], `signature-${receiptKey}-${Date.now()}.png`, { type: 'image/png' })
        const cloudPath = `receipts/signature-${receiptKey}-${Date.now()}.png`
        const uploadRes: any = await app.uploadFile({ cloudPath, filePath: file } as any)
        signatureUrl = uploadRes?.fileID || ''
      } else if (signatureList.length && signatureList[0].originFileObj) {
        const file = signatureList[0].originFileObj
        const cloudPath = `receipts/signature-${receiptKey}-${Date.now()}-${file.name}`
        const uploadRes: any = await app.uploadFile({ cloudPath, filePath: file } as any)
        signatureUrl = uploadRes?.fileID || ''
      }

      const raw = currentReceipt.rawReceipt || {}
      const items = Array.isArray(raw.items) && raw.items.length ? raw.items : [{ ...raw }]
      const verifiedAt = new Date().toISOString()
      const newItems = items.map((it: any) => ({
        ...it,
        receivedAmount: Number(it.receivedAmount ?? it.expectedAmount ?? it.amount ?? 0),
        status: 'cashier_done',
        verifiedAt,
        cashierId: user?.id,
        cashierName: user?.nickName,
        cashierSignatureUrl: signatureUrl,
        cashierNotes,
        cashierSignatureReceiptId: receiptKey
      }))

      const res = await simpleDirectCloudApi.updateReceipt(currentReceipt.receiptId, {
        status: 'cashier_done',
        items: newItems,
        verifiedAt,
        cashierId: user?.id,
        cashierName: user?.nickName,
        cashierSignatureUrl: signatureUrl,
        cashierNotes,
        cashierSignatureReceiptId: receiptKey
      })
      if (res.success) {
        message.success('已确认收款')
        setConfirmVisible(false)
        setCurrentReceipt(null)
        setSignatureList([])
        setSignatureDataUrl('')
        setCashierNotes('')
        clearPad()
        await fetchVehicles().catch(() => {})
        loadData()
      } else {
        message.error(res.error || '确认失败')
      }
    } catch (error) {
      console.error(error)
      message.error('确认收款失败')
    }
  }

  const handleSubmitLeaseConfirm = async () => {
    if (!currentLeaseForm) return
    if ((!leaseSignatureList.length || !leaseSignatureList[0].originFileObj) && !leaseSignatureDataUrl) {
      message.error('请上传签名')
      return
    }
    try {
      const leaseKey = currentLeaseForm._id || currentLeaseForm.id || currentLeaseForm.vehicleId
      let signatureUrl = ''
      if (leaseSignatureDataUrl) {
        const blob = await (await fetch(leaseSignatureDataUrl)).blob()
        const file = new File([blob], `lease-signature-${leaseKey}-${Date.now()}.png`, { type: 'image/png' })
        const cloudPath = `lease_forms/signature-${leaseKey}-${Date.now()}.png`
        const uploadRes: any = await app.uploadFile({ cloudPath, filePath: file } as any)
        signatureUrl = uploadRes?.fileID || ''
      } else if (leaseSignatureList.length && leaseSignatureList[0].originFileObj) {
        const file = leaseSignatureList[0].originFileObj
        const cloudPath = `lease_forms/signature-${leaseKey}-${Date.now()}-${file.name}`
        const uploadRes: any = await app.uploadFile({ cloudPath, filePath: file } as any)
        signatureUrl = uploadRes?.fileID || ''
      }

      const signedAt = new Date().toISOString()
      const res = await simpleDirectCloudApi.updateLeaseForm(currentLeaseForm._id || currentLeaseForm.id, {
        status: 'signed',
        cashierId: user?.id,
        cashierName: user?.nickName,
        cashierSignatureUrl: signatureUrl,
        signedAt,
        cashierNotes: leaseNotes
      })
      if (res.success) {
        if (currentLeaseForm.type === 'return' && currentLeaseForm.vehicleId) {
          await simpleDirectCloudApi.updateVehicle(currentLeaseForm.vehicleId, {
            _id: currentLeaseForm.vehicleId,
            status: 'maintenance',
            returnSigned: true
          } as any)
        }
        message.success('已核验签名')
        setLeaseConfirmVisible(false)
        setCurrentLeaseForm(null)
        setLeaseSignatureList([])
        setLeaseSignatureDataUrl('')
        setLeaseNotes('')
        clearLeasePad()
        await fetchVehicles().catch(() => {})
        loadData()
      } else {
        message.error(res.error || '核验失败')
      }
    } catch (error) {
      console.error(error)
      message.error('核验失败')
    }
  }

  const handleRevokeReceipt = async (receipt: any) => {
    const raw = receipt.rawReceipt || {}
    const items = Array.isArray(raw.items) && raw.items.length ? raw.items : [{ ...raw }]
    const resetItems = items.map((it: any) => ({
      ...it,
      status: 'submitted',
      verifiedAmount: undefined,
      receivedAmount: it.expectedAmount ?? it.amount ?? it.receivedAmount,
      verifiedAt: undefined,
      cashierId: undefined,
      cashierName: undefined,
      cashierSignatureUrl: undefined,
      cashierNotes: undefined
    }))
    const res = await simpleDirectCloudApi.updateReceipt(receipt.receiptId, {
      status: 'submitted',
      items: resetItems,
      verifiedAt: undefined,
      cashierId: undefined,
      cashierName: undefined,
      cashierSignatureUrl: undefined,
      cashierNotes: undefined
    })
    if (res.success) {
      message.success('已撤销收款')
      await fetchVehicles().catch(() => {})
      loadData()
    } else {
      message.error(res.error || '撤销失败')
    }
  }

  if (loading) {
      return (
        <div style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Spin size="large" />
        </div>
      )
    }

  return (
    <div style={{ padding: '24px', background: 'var(--bg)', minHeight: '100vh' }}>
      <Card style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space size="large">
              <Avatar size={64} style={{ backgroundColor: 'var(--warning)' }}>CASH</Avatar>
              <div>
                <Title level={4} style={{ margin: 0 }}>{user?.nickname || user?.nickName || user?.username || 'cashier'}</Title>
                  <Text type="secondary">{user?.info || '收款员'}</Text>
              </div>
            </Space>
          </Col>
          <Col>
            <Button icon={<LogoutOutlined />} onClick={handleLogout}>退出登录</Button>
          </Col>
        </Row>
      </Card>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="待确认" value={statistics.pendingVerification} valueStyle={{ color: 'var(--accent)' }} prefix={<AuditOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="今日确认" value={statistics.verifiedToday} valueStyle={{ color: 'var(--positive)' }} prefix={<CheckCircleOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="待收金额" value={statistics.pendingAmount} valueStyle={{ color: 'var(--warning)' }} prefix={<DollarOutlined />} formatter={(v) => `￥${Number(v || 0).toFixed(2)}`} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="累计收款" value={statistics.totalProcessed} valueStyle={{ color: '#52c41a' }} prefix={<DollarOutlined />} formatter={(v) => `￥${Number(v || 0).toFixed(2)}`} />
          </Card>
        </Col>
      </Row>

      <Card
        style={{
          marginBottom: 24,
          borderColor: (pendingReturnForms.length || maintenanceWithoutSignedReturnDoc.length) ? 'var(--warning)' : 'var(--line)',
          background: (pendingReturnForms.length || maintenanceWithoutSignedReturnDoc.length) ? 'rgba(192, 122, 31, 0.06)' : undefined
        }}
      >
        <Row justify="space-between" align="middle">
          <Col>
            <Space wrap>
              <Tag color={pendingReturnForms.length ? 'orange' : 'default'}>
                待核验退租单 {pendingReturnForms.length}
              </Tag>
              <Tag color={maintenanceWithoutSignedReturnDoc.length ? 'red' : 'default'}>
                退租状态缺单据 {maintenanceWithoutSignedReturnDoc.length}
              </Tag>
              <Text type="secondary">
                用于核对“车辆已退租”是否有已签单据，防止绕过流程。
              </Text>
            </Space>
          </Col>
          <Col>
            <Button onClick={() => setActiveTab('lease')}>
              去核验退租/出租单
            </Button>
          </Col>
        </Row>
      </Card>

      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'pending',
              label: (
                <Badge count={pendingReceipts.length} size="small">
                  <span>待收款</span>
                </Badge>
              ),
              children: (
                <List
                  itemLayout="vertical"
                  dataSource={pendingReceipts}
                  renderItem={(item) => (
                      <List.Item
                        actions={[
                          <Button key="preview" type="primary" onClick={() => openReceiptPreview(item, 'pending')}>
                            查看凭证
                          </Button>
                        ]}
                      >
                        <List.Item.Meta
                          avatar={<Avatar style={{ backgroundColor: 'var(--accent)' }}>{item.plate}</Avatar>}
                          title={
                            <Space>
                              <Text strong>{item.plate}</Text>
                              <Tag color="orange">待收款</Tag>
                            </Space>
                          }
                          description={
                            <div>
                              <Row gutter={16}>
                                <Col span={12}><Text type="secondary">车型</Text> <Text>{item.model}</Text></Col>
                                <Col span={12}><Text type="secondary">公司</Text> <Text>{item.company}</Text></Col>
                              </Row>
                              <Row gutter={16}>
                                <Col span={12}><Text type="secondary">金额</Text> <Text strong style={{ color: 'var(--positive)' }}>￥{item.amount}</Text></Col>
                                <Col span={12}><Text type="secondary">到期日</Text> <Text>{item.reminderDue || item.dueDate || '-'}</Text></Col>
                              </Row>
                              <Row gutter={16}>
                                <Col span={12}><Text type="secondary">提醒周期</Text> <Tag>{getPeriodLabel(item.reminderPeriod, item.reminderPeriodMonths)}</Tag></Col>
                                <Col span={12}><Text type="secondary">提醒金额</Text> <Text>￥{Number(item.reminderAmount ?? item.amount ?? 0).toFixed(2)}</Text></Col>
                              </Row>
                              <Row gutter={16}>
                                <Col span={12}><Text type="secondary">制单人</Text> <Text>{item.createdBy || '-'}</Text></Col>
                              </Row>
                              {getRangeLabel(item) && (
                                <Row gutter={16}>
                                  <Col span={24}><Text type="secondary">收款区间</Text> <Text>{getRangeLabel(item)}</Text></Col>
                                </Row>
                              )}
                            </div>
                          }
                        />
                      </List.Item>
                    )}
                />
              )
            },
            {
              key: 'lease',
              label: (
                <Badge count={leaseForms.length} size="small">
                  <span>退租/出租单</span>
                </Badge>
              ),
              children: (
                <List
                  itemLayout="vertical"
                  dataSource={leaseForms}
                  renderItem={(item) => (
                    <List.Item
                      actions={[
                        <Button key="view" onClick={() => openLeasePreview(item)}>
                          查看单据
                        </Button>,
                        <Button key="sign" type="primary" onClick={() => handleConfirmLease(item)}>
                          核验签名
                        </Button>
                      ]}
                    >
                      <List.Item.Meta
                        avatar={<Avatar style={{ backgroundColor: 'var(--accent)' }}>{item.plate || '-'}</Avatar>}
                        title={(
                          <Space>
                            <Text strong>{item.plate || '-'}</Text>
                            <Tag color={item.type === 'return' ? 'volcano' : 'blue'}>
                              {item.type === 'return' ? '退租单' : '出租单'}
                            </Tag>
                          </Space>
                        )}
                        description={(
                          <div>
                            <Row gutter={16}>
                              <Col span={12}><Text type="secondary">公司</Text> <Text>{item.company || '-'}</Text></Col>
                              <Col span={12}><Text type="secondary">{item.type === 'relet' ? '出租单位' : '退租单位'}</Text> <Text>{item.unitName || '-'}</Text></Col>
                            </Row>
                            <Row gutter={16}>
                              <Col span={12}><Text type="secondary">提交人</Text> <Text>{item.createdBy || '-'}</Text></Col>
                              <Col span={12}><Text type="secondary">提交时间</Text> <Text>{item.createdAt ? dayjs(item.createdAt).format('YYYY-MM-DD HH:mm') : '-'}</Text></Col>
                            </Row>
                            {item.notes ? (
                              <Row gutter={16}>
                                <Col span={24}><Text type="secondary">备注</Text> <Text>{item.notes}</Text></Col>
                              </Row>
                            ) : null}
                          </div>
                        )}
                      />
                    </List.Item>
                  )}
                />
              )
            },
            {
              key: 'lease-signed',
              label: (
                <Badge count={signedLeaseForms.length} size="small">
                  <span>已签退租/出租单</span>
                </Badge>
              ),
              children: (
                <List
                  itemLayout="vertical"
                  dataSource={signedLeaseForms}
                  renderItem={(item) => (
                    <List.Item
                      actions={[
                        <Button key="view" onClick={() => openLeasePreview(item)}>
                          查看单据
                        </Button>
                      ]}
                    >
                      <List.Item.Meta
                        avatar={<Avatar style={{ backgroundColor: 'var(--positive)' }}>{item.plate || '-'}</Avatar>}
                        title={(
                          <Space>
                            <Text strong>{item.plate || '-'}</Text>
                            <Tag color="green">已签名</Tag>
                            <Tag color={item.type === 'return' ? 'volcano' : 'blue'}>
                              {item.type === 'return' ? '退租单' : '出租单'}
                            </Tag>
                          </Space>
                        )}
                        description={(
                          <div>
                            <Row gutter={16}>
                              <Col span={12}><Text type="secondary">公司</Text> <Text>{item.company || '-'}</Text></Col>
                              <Col span={12}><Text type="secondary">{item.type === 'relet' ? '出租单位' : '退租单位'}</Text> <Text>{item.unitName || '-'}</Text></Col>
                            </Row>
                            <Row gutter={16}>
                              <Col span={12}><Text type="secondary">签名人</Text> <Text>{item.cashierName || '-'}</Text></Col>
                              <Col span={12}><Text type="secondary">签名时间</Text> <Text>{item.signedAt ? dayjs(item.signedAt).format('YYYY-MM-DD HH:mm') : '-'}</Text></Col>
                            </Row>
                          </div>
                        )}
                      />
                    </List.Item>
                  )}
                />
              )
            },
            {
              key: 'verified',
              label: (
                <Badge count={verifiedReceipts.length} size="small">
                  <span>已收款</span>
                </Badge>
              ),
              children: (
                <List
                  itemLayout="vertical"
                  dataSource={verifiedReceipts}
                  renderItem={(item) => (
                      <List.Item
                        actions={[
                          <Button key="receipt" onClick={() => openReceiptPreview(item, 'verified')}>
                            查看收据
                          </Button>,
                          <Button key="revoke" danger onClick={() => handleRevokeReceipt(item)}>
                            撤销
                          </Button>
                        ]}
                      >
                        <List.Item.Meta
                          avatar={<Avatar style={{ backgroundColor: 'var(--positive)' }}>{item.plate}</Avatar>}
                          title={
                            <Space>
                              <Text strong>{item.plate}</Text>
                              <Tag color="green">已收款</Tag>
                            </Space>
                          }
                          description={
                            <div>
                            <Row gutter={16}>
                              <Col span={12}><Text type="secondary">公司</Text> <Text>{item.company}</Text></Col>
                              <Col span={12}><Text type="secondary">周期</Text> <Tag>{item.periodKey || '周期'}</Tag></Col>
                            </Row>
                            <Row gutter={16}>
                              <Col span={12}><Text type="secondary">制单人</Text> <Text>{item.createdBy || '-'}</Text></Col>
                            </Row>
                            <Text type="secondary">金额</Text> <Text strong>￥{item.amount}</Text><br />
                            <Text type="secondary">确认时间</Text> <Text>{item.verifiedAt ? dayjs(item.verifiedAt).format('YYYY-MM-DD HH:mm') : '-'}</Text>
                            {getRangeLabel(item) && (
                              <>
                                <br />
                                <Text type="secondary">扣款范围</Text> <Text>{getRangeLabel(item)}</Text>
                              </>
                            )}
                            </div>
                          }
                        />
                      </List.Item>
                    )}
                />
              )
            }
          ]}
        />
      </Card>

      <Card
        style={{
          marginBottom: 24,
          borderColor: expiringVehiclesAll.length ? 'var(--warning)' : 'var(--line)',
          background: expiringVehiclesAll.length ? 'rgba(192, 122, 31, 0.06)' : undefined
        }}
      >
        <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
          <Col>
            <Space>
              <ClockCircleOutlined style={{ color: 'var(--warning)' }} />
              <Text strong>到期提醒</Text>
              <Tag color={expiringVehiclesAll.length ? 'orange' : 'default'}>
                {expiringVehiclesAll.length} 台
              </Tag>
              {expiringVehiclesAll.length ? (
                <Text type="secondary">请优先核对即将到期的车辆</Text>
              ) : (
                <Text type="secondary">当前没有需要提醒的车辆</Text>
              )}
            </Space>
          </Col>
          <Col>
            <Button type="primary" onClick={() => setExpiringModalVisible(true)} disabled={!expiringVehiclesAll.length}>
              查看全部
            </Button>
          </Col>
        </Row>
        {expiringVehiclesAll.length > 0 && (
          <List
            itemLayout="vertical"
            dataSource={expiringTop}
            renderItem={(vehicle) => {
              const dueDate = getVehicleDueDate(vehicle)
              return (
                <List.Item key={vehicle._id || vehicle.id || vehicle.plate}>
                  <List.Item.Meta
                    avatar={<Avatar style={{ backgroundColor: '#cfa972' }}>{vehicle.plate}</Avatar>}
                    title={
                      <Space>
                        <Text strong>{vehicle.plate}</Text>
                        <Tag color="orange">{dueDate ? dayjs(dueDate).format('YYYY-MM-DD') : '-'}</Tag>
                        <Tag color="gold">租金 ¥{Number(vehicle.reminderAmount ?? vehicle.rentAmount ?? 0).toFixed(2)}</Tag>
                      </Space>
                    }
                    description={
                      <div>
                        <Text type="secondary">公司：</Text>
                        <Text>{vehicle.company || '-'}</Text>
                        <Text type="secondary" style={{ marginLeft: 12 }}>联系人：</Text>
                        <Text>{vehicle.contactPerson || '-'}</Text>
                      </div>
                    }
                  />
                </List.Item>
              )
            }}
          />
        )}
      </Card>

      <Modal
        title="确认收款"
        open={confirmVisible}
        onOk={handleSubmitConfirm}
        onCancel={() => { setConfirmVisible(false); setCurrentReceipt(null); setSignatureList([]); setSignatureDataUrl(''); setCashierNotes(''); clearPad() }}
        okText="确认收款"
      >
        {currentReceipt && (
          <div>
            <p>车牌:{currentReceipt.plate}</p>
            <p>金额:￥{currentReceipt.amount}</p>
            {getRangeLabel(currentReceipt) && (
              <p>收款区间:{getRangeLabel(currentReceipt)}</p>
            )}
            <div style={{ marginBottom: 8 }}>
              <div style={{ border: '1px solid #ddd', borderRadius: 4, width: '100%', height: 180, marginBottom: 8 }}>
                <canvas
                  ref={canvasRef}
                  width={1}
                  height={CANVAS_HEIGHT}
                  style={{ width: '100%', height: CANVAS_HEIGHT, touchAction: 'none', display: 'block' }}
                  onPointerDown={startDraw}
                  onPointerMove={draw}
                  onPointerUp={endDraw}
                  onPointerLeave={endDraw}
                  onPointerCancel={endDraw}
                />
              </div>
              <Space style={{ marginBottom: 8 }}>
                <Button onClick={clearPad}>清空签名</Button>
                {signatureDataUrl && <Tag color="green">已签名</Tag>}
              </Space>
            </div>
            <Upload
              listType="picture-card"
              fileList={signatureList}
              beforeUpload={() => false}
              onChange={({ fileList }) => setSignatureList(fileList)}
              maxCount={1}
            >
              {signatureList.length >= 1 ? null : '上传签名/附件'}
            </Upload>
            <Input.TextArea
              rows={3}
              placeholder="备注(可选)"
              value={cashierNotes}
              onChange={(e) => setCashierNotes(e.target.value)}
            />
          </div>
        )}
      </Modal>
      <Modal
        title="核验退租/出租单"
        open={leaseConfirmVisible}
        onOk={handleSubmitLeaseConfirm}
        onCancel={() => { setLeaseConfirmVisible(false); setCurrentLeaseForm(null); setLeaseSignatureList([]); setLeaseSignatureDataUrl(''); setLeaseNotes(''); clearLeasePad() }}
        okText="确认签名"
      >
        {currentLeaseForm && (
          <div>
            <p>车牌:{currentLeaseForm.plate || '-'}</p>
            <p>{currentLeaseForm.type === 'relet' ? '出租单位' : '退租单位'}:{currentLeaseForm.unitName || '-'}</p>
            <div style={{ marginBottom: 8 }}>
              <div style={{ border: '1px solid #ddd', borderRadius: 4, width: '100%', height: 180, marginBottom: 8 }}>
                <canvas
                  ref={leaseCanvasRef}
                  width={1}
                  height={CANVAS_HEIGHT}
                  style={{ width: '100%', height: CANVAS_HEIGHT, touchAction: 'none', display: 'block' }}
                  onPointerDown={startLeaseDraw}
                  onPointerMove={drawLease}
                  onPointerUp={endLeaseDraw}
                  onPointerLeave={endLeaseDraw}
                  onPointerCancel={endLeaseDraw}
                />
              </div>
              <Space style={{ marginBottom: 8 }}>
                <Button onClick={clearLeasePad}>清空签名</Button>
                {leaseSignatureDataUrl && <Tag color="green">已签名</Tag>}
              </Space>
            </div>
            <Upload
              listType="picture-card"
              fileList={leaseSignatureList}
              beforeUpload={() => false}
              onChange={({ fileList }) => setLeaseSignatureList(fileList)}
              maxCount={1}
            >
              {leaseSignatureList.length >= 1 ? null : '上传签名/附件'}
            </Upload>
            <Input.TextArea
              rows={3}
              placeholder="备注(可选)"
              value={leaseNotes}
              onChange={(e) => setLeaseNotes(e.target.value)}
            />
          </div>
        )}
      </Modal>
      <Modal
        title={receiptPreviewMode === 'pending' ? '收款凭证' : '收据'}
        open={receiptPreviewVisible}
        onCancel={() => { setReceiptPreviewVisible(false); setReceiptPreview(null) }}
        footer={
          receiptPreviewMode === 'pending'
            ? [
                <Button key="cancel" onClick={() => { setReceiptPreviewVisible(false); setReceiptPreview(null) }}>
                  取消
                </Button>,
                <Button
                  key="confirm"
                  type="primary"
                  onClick={() => {
                    if (receiptPreview) {
                      setReceiptPreviewVisible(false)
                      handleConfirmReceipt(receiptPreview)
                    }
                  }}
                >
                  确认收款
                </Button>
              ]
            : null
        }
        width={720}
      >
        {receiptPreview && (
          (() => {
            const items = getReceiptItems(receiptPreview.rawReceipt || receiptPreview)
            const total = items.reduce((sum: number, it: any) =>
              sum + Number(it.receivedAmount ?? it.expectedAmount ?? it.amount ?? 0), 0)
            const receiptNo = receiptPreview.receiptId || receiptPreview._id || receiptPreview.id || '-'
            const date = formatDate(receiptPreview.verifiedAt || receiptPreview.createdAt)
            const rangeLabel = getRangeLabel(receiptPreview)
            const cashierNoteValue = receiptPreview.cashierNotes ||
              receiptPreview.rawReceipt?.cashierNotes ||
              receiptPreview.notes ||
              receiptPreview.rawReceipt?.notes ||
              ''
            const actualAmountValue = receiptPreview.actualAmount ?? receiptPreview.rawReceipt?.actualAmount
            const company = receiptPreview.company || receiptPreview.customerName || '-'
            const makerName =
              receiptPreview.createdBy ||
              receiptPreview.rawReceipt?.createdBy ||
              '-'
            const makerSeal =
              receiptPreview.makerSealUrl ||
              receiptPreview.rawReceipt?.makerSealUrl ||
              ''
            return (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                  <div>制单人：{makerName}</div>
                  <Button onClick={handleExportReceipt}>导出/打印</Button>
                </div>
                <div className="receipt-preview-scroll">
                  <div
                    ref={receiptPrintRef}
                    className="receipt-preview-sheet"
                    style={{
                      fontFamily: '"SimSun","STSong","Noto Serif SC",serif',
                      color: '#222'
                    }}
                  >
                    <div style={{ textAlign: 'center', fontSize: 28, fontWeight: 700, letterSpacing: 8 }}>
                      收款收据
                    </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
                    <div>客户名称：{company}</div>
                    <div>No：{receiptNo}</div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                    <div>收款范围：{rangeLabel || '-'}</div>
                    <div>日期：{date}</div>
                  </div>
                  <div style={{ border: '1px solid #333', marginTop: 10 }}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '80px 1fr 150px 60px 60px 90px 110px',
                      fontWeight: 700,
                      borderBottom: '1px solid #333'
                    }}>
                      <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>货号</div>
                      <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>品名及规格</div>
                      <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>收款区间</div>
                      <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>单位</div>
                      <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>数量</div>
                      <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>单价</div>
                      <div style={{ padding: '6px 8px' }}>金额</div>
                    </div>
                    {items.map((it: any, idx: number) => {
                      const amount = Number(it.receivedAmount ?? it.expectedAmount ?? it.amount ?? 0)
                      const itemRange = getRangeLabel(it)
                      return (
                        <div
                          key={idx}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '80px 1fr 150px 60px 60px 90px 110px',
                            borderBottom: idx === items.length - 1 ? 'none' : '1px solid #333'
                          }}
                        >
                          <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }} />
                          <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>
                            车辆租金（{it.plate || receiptPreview.plate || '-'}）
                          </div>
                          <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>{itemRange || '-'}</div>
                          <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>台</div>
                          <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>1</div>
                          <div style={{ padding: '6px 8px', borderRight: '1px solid #333', textAlign: 'right' }}>
                            {formatMoney(amount)}
                          </div>
                          <div style={{ padding: '6px 8px', textAlign: 'right' }}>{formatMoney(amount)}</div>
                        </div>
                      )
                    })}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
                    <div>合计人民币：{formatMoney(total)}</div>
                    <div>￥{formatMoney(total)}</div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                    <div>实际收入：{actualAmountValue != null ? formatMoney(actualAmountValue) : '-'}</div>
                    <div>￥{actualAmountValue != null ? formatMoney(actualAmountValue) : '-'}</div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
                    <div>制单人：{makerName}</div>
                    <div>会计：</div>
                    <div>出纳：{receiptPreview.cashierName || user?.nickName || '-'}</div>
                    <div>出纳单位：</div>
                  </div>
                  {makerSeal ? (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                      <img
                        src={makerSeal}
                        alt="maker seal"
                        style={{ maxWidth: 220, maxHeight: 120, border: '1px solid #ddd', borderRadius: 4 }}
                      />
                    </div>
                  ) : null}
                  <div style={{ marginTop: 8 }}>备注：{cashierNoteValue || '-'}</div>
                  {receiptPreviewMode === 'verified' && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                      {receiptSignatureUrl ? (
                        <img
                          src={receiptSignatureUrl}
                          alt="signature"
                          style={{ maxWidth: 220, maxHeight: 120, border: '1px solid #ddd', borderRadius: 4 }}
                        />
                      ) : (
                        <div style={{ width: 220, height: 120, border: '1px dashed #ccc' }} />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            )
          })()
        )}
      </Modal>
      <Modal
        title={leasePreview?.type === 'return' ? '退租单' : '出租单'}
        open={leasePreviewVisible}
        onCancel={() => { setLeasePreviewVisible(false); setLeasePreview(null) }}
        footer={null}
        width={720}
      >
        {leasePreview && (
          <div>
            <div style={{ textAlign: 'center', fontSize: 28, fontWeight: 700, letterSpacing: 8 }}>
              {leasePreview.type === 'return' ? '退租单' : '出租单'}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
              <div>{leasePreview.type === 'relet' ? '出租单位' : '退租单位'}：{leasePreview.unitName || '-'}</div>
              <div>No：{leasePreview._id || leasePreview.id || '-'}</div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
              <div>车牌：{leasePreview.plate || '-'}</div>
              <div>日期：{leasePreview.createdAt ? dayjs(leasePreview.createdAt).format('YYYY-MM-DD') : '-'}</div>
            </div>
            <div style={{ border: '1px solid #333', marginTop: 10 }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '80px 1fr 150px 60px 60px 90px 110px',
                fontWeight: 700,
                borderBottom: '1px solid #333'
              }}>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>货号</div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>品名及规格</div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>{leasePreview.type === 'relet' ? '出租单位' : '退租单位'}</div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>单位</div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>数量</div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>单价</div>
                <div style={{ padding: '6px 8px' }}>金额</div>
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '80px 1fr 150px 60px 60px 90px 110px'
              }}>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }} />
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>
                  {(() => {
                    const plate = leasePreview.plate || '-'
                    const model = leasePreview.model ? ` - ${leasePreview.model}` : ''
                    return `车辆租赁（${plate}）${model}`
                  })()}
                </div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>{leasePreview.unitName || '-'}</div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>台</div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>1</div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333', textAlign: 'right' }}>-</div>
                <div style={{ padding: '6px 8px', textAlign: 'right' }}>-</div>
              </div>
            </div>
            <div style={{ marginTop: 8 }}>
              <div>配置：{leasePreview.configuration || '-'}</div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
              <div>备注：{leasePreview.notes || '-'}</div>
              <div>状态：{leasePreview.status === 'signed' ? '已签名' : '待签名'}</div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
              <div>制单人：{leasePreview.createdBy || '-'}</div>
              <div>会计：</div>
              <div>出纳：{leasePreview.cashierName || '-'}</div>
              <div>出纳单位：</div>
            </div>
            {leasePreview.makerSealUrl && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                <img
                  src={leasePreview.makerSealUrl}
                  alt="maker seal"
                  style={{ maxWidth: 220, maxHeight: 120, border: '1px solid #ddd', borderRadius: 4 }}
                />
              </div>
            )}
            {leasePreview.cashierSignatureUrl && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                {leaseSignatureUrl ? (
                  <img
                    src={leaseSignatureUrl}
                    alt="signature"
                    style={{ maxWidth: 220, maxHeight: 120, border: '1px solid #ddd', borderRadius: 4 }}
                  />
                ) : (
                  <div style={{ width: 220, height: 120, border: '1px dashed #ccc' }} />
                )}
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        title="到期提醒"
        open={expiringModalVisible}
        onCancel={() => setExpiringModalVisible(false)}
        footer={null}
        width={720}
      >
        <List
          itemLayout="vertical"
          dataSource={expiringVehiclesAll}
          renderItem={(vehicle) => {
            const dueDate = getVehicleDueDate(vehicle)
            return (
              <List.Item key={vehicle._id || vehicle.id || vehicle.plate}>
                <List.Item.Meta
                  avatar={<Avatar style={{ backgroundColor: '#cfa972' }}>{vehicle.plate}</Avatar>}
                  title={
                    <Space>
                      <Text strong>{vehicle.plate}</Text>
                      <Tag color="orange">{dueDate ? dayjs(dueDate).format('YYYY-MM-DD') : '-'}</Tag>
                      <Tag color="gold">租金 ¥{Number(vehicle.reminderAmount ?? vehicle.rentAmount ?? 0).toFixed(2)}</Tag>
                    </Space>
                  }
                  description={
                    <div>
                      <Text type="secondary">公司：</Text>
                      <Text>{vehicle.company || '-'}</Text>
                      <Text type="secondary" style={{ marginLeft: 12 }}>联系人：</Text>
                      <Text>{vehicle.contactPerson || '-'}</Text>
                    </div>
                  }
                />
              </List.Item>
            )
          }}
        />
      </Modal>
    </div>
  )
}

export default CashierPage





